import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { FileText, Check, Info } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="text-blue-600 hover:underline text-sm">← Back to WANTTD</Link>
          </div>
          
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Legal</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink>Terms of Service</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <Card className="shadow-sm border border-gray-200">
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-6 w-6 text-gray-700" />
                <CardTitle className="text-2xl font-bold text-gray-800">WANTTD TERMS OF SERVICE</CardTitle>
              </div>
              <p className="text-sm text-gray-500">Last Updated: April 2025</p>
            </CardHeader>
            
            <CardContent className="pt-6">
              <div className="space-y-6">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger className="text-lg font-medium">1. Introduction</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p className="mb-4">
                        Welcome to WANTTD ("Company," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use 
                        of the WANTTD platform, including our website, applications, and related services (collectively, the "Service"). By accessing, browsing, or using the Service in any manner, including creating an account, posting content, or engaging with other users, you acknowledge that you have read, understood, and agree to be legally bound by these Terms and all applicable laws. If you do not agree to these Terms, you must discontinue your use of the Service immediately.
                      </p>
                      <p>
                        These Terms incorporate by reference our Privacy Policy, Community Standards, and any additional policies or guidelines we issue periodically. We reserve the right to modify or update these Terms at any time, and continued use of the Service after changes constitutes acceptance of the revised Terms.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="item-2">
                    <AccordionTrigger className="text-lg font-medium">2. Definitions</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        A Wanter is a registered user who posts wills, wishes, desires or requests on the platform, seeking fulfillment from other users. A Fulfiller (Genie) is a registered user who offers services or products to fulfill a Wanter's request. A Desire Posting is a listing created by a Wanter describing a specific will, wish, desire, need, goal, or service they seek. An Offer is a proposal submitted by a Fulfiller in response to a Wanter's request, outlining how they can fulfill the request and on what terms. An Order is a binding agreement between a Wanter and a Fulfiller initiated through the platform for the provision of a service or exchange of goods. A Transaction is any agreement, whether monetary or otherwise, executed between a Wanter and a Fulfiller. The Platform refers to the digital infrastructure provided by WANTTD to facilitate interactions between Wanters and Fulfillers.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="item-3">
                    <AccordionTrigger className="text-lg font-medium">3. User Eligibility and Obligations</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        To use the Service, you must be at least 18 years old or the legal age of majority in your jurisdiction. You must also have the capacity to enter into legally binding contracts, must not be previously banned or suspended from using the Service, and must not reside in a country where use of the Service is prohibited by law. By using the Service, you agree that you will not engage in fraudulent, deceptive, or misleading activities. You will not post false, misleading, or defamatory content, infringe upon any third party's intellectual property rights, violate any local, national, or international laws, circumvent the payment or communication systems provided by the platform, engage in abusive, harassing, or harmful conduct toward other users, or attempt to hack, manipulate, or interfere with the functionality of the platform.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4">
                    <AccordionTrigger className="text-lg font-medium">4. Account Registration and Security</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        To access certain features of the Service, users must create an account by providing valid and accurate information, including but not limited to a name, email address, and payment details where applicable. Users are responsible for maintaining the confidentiality of their login credentials. WANTTD is not liable for any loss or damage resulting from unauthorized use of your account. If you suspect unauthorized activity, you must notify WANTTD immediately. WANTTD reserves the right to suspend, restrict, or terminate any account without prior notice for violations of these Terms, fraudulent activity, or illegal conduct. Users may delete their accounts by contacting customer support; however, obligations incurred before termination will remain in effect.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-5">
                    <AccordionTrigger className="text-lg font-medium">5. Services and Transactions</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        WANTTD acts solely as a facilitator of connections between users. It does not endorse, guarantee, or assume responsibility for the accuracy, quality, legality, or reliability of services or products exchanged between users. Users agree to pay applicable fees for services rendered through the platform. WANTTD may charge service or transaction fees, which will be clearly disclosed before completing an order. Payments are processed via third-party payment gateways, and WANTTD is not responsible for transaction failures, chargebacks, or payment disputes. Refunds, where applicable, will be handled in accordance with WANTTD's refund policy.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6">
                    <AccordionTrigger className="text-lg font-medium">6. Dispute Resolution</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        WANTTD is not responsible for disputes between users but may, at its sole discretion, offer mediation services. Users are encouraged to resolve disputes through direct communication. If disputes cannot be settled independently, they may be escalated to external legal channels.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7">
                    <AccordionTrigger className="text-lg font-medium">7. Intellectual Property</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        Users retain ownership of any content they create but grant WANTTD a non-exclusive, worldwide, royalty-free license to use, reproduce, distribute, and display such content within the platform for marketing and operational purposes. Users may not post content that contains offensive, discriminatory, or defamatory language, promotes illegal activities or violates any laws, infringes on copyrights, trademarks, or other intellectual property rights, or contains sexually explicit or adult content.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8">
                    <AccordionTrigger className="text-lg font-medium">8. Limitation of Liability</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        To the fullest extent permitted by law, WANTTD, its affiliates, officers, employees, and agents shall not be liable for indirect, incidental, or consequential damages arising from platform use. WANTTD shall not be responsible for any loss of profits, revenue, or data resulting from disputes between users or any interruptions, delays, or errors in platform functionality.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-9">
                    <AccordionTrigger className="text-lg font-medium">9. Termination and Enforcement</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                      WANTTD reserves the right to suspend, terminate, or restrict access to any user who violates these Terms. Users who wish to terminate their accounts may do so, but any outstanding obligations must be fulfilled prior to closure.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-10">
                    <AccordionTrigger className="text-lg font-medium">10. Governing Law</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                      These Terms shall be governed by and construed in accordance with Israeli law. Any disputes arising from these Terms shall be exclusively settled in the courts of Tel Aviv, Israel.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-11">
                    <AccordionTrigger className="text-lg font-medium">11. Updates to Terms</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                      WANTTD reserves the right to update these Terms at any time. Users will be notified of significant changes, and continued use of the Service constitutes acceptance of the updated Terms.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-12">
                    <AccordionTrigger className="text-lg font-medium">12. Contact Information</AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      <p>
                        For inquiries, complaints, or legal notices, contact us at: support@wanttd.com
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900">Your Agreement</h3>
                    <p className="text-sm text-gray-600">
                      By using WANTTD, you confirm that you have read, understood, and agree to be bound by these Terms of Service.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
